import { getUuid } from '@sibe/utilities/common';
import type { HsGraphics } from '../../graphic-render-new/graphics.class';
import type { HsGraphicQueue } from '../../graphic-render-new/objects/graphic-queue.class';
import { HsHypersceneConstants } from '../../math-new/constants/hyperscene-constants.global';
import type { HsColor } from '../../math-new/objects/color.class';
import { HsVector3 } from '../../math-new/objects/vector3.class';
import { HsPlane } from '../../math-new/shapes/plane.class';
import type { HsHypersceneTypes } from '../../math-new/types/hyperscene-types.type';
import { HsObject3D } from '../../engine-new/objects/common/object-3d.class';
import type { HsGraphicObject } from '../../graphic-render-new/objects/graphic-object.class';
import { HsMatrix4 } from '../../math-new/objects/matrix4.class';
import { HsPrimitives } from '../primitives/primitives.class';
import { HsArrowTool } from './arrow-tool.class';
import type { HsControlTool } from './control-tool.class';
import { HsPlaneTool } from './plane-tool.class';
import type { HsToolAction } from './tool-action.class';

export class HsMoveTool extends HsObject3D {
  private readonly arrows: HsControlTool[] = [];
  private readonly planes: HsControlTool[] = [];
  private readonly allTools: HsControlTool[] = [];

  private readonly objectsMap: Map<HsGraphicObject, HsMatrix4> = new Map();

  private readonly target: HsVector3 = HsVector3.makeZeroVector();
  private readonly originalTarget: HsVector3 = HsVector3.makeZeroVector();
  private readonly originalMatrix: HsMatrix4 = HsMatrix4.makeIdentityMatrix();

  private readonly position: HsVector3 = HsVector3.makeZeroVector();

  private needToSetOrigin: boolean = false;

  private plane: HsPlane = HsPlane.makePlaneXY();

  private readonly primitives: HsPrimitives;

  /**
   * The tools are not added to `queue`: hosts add `getArrowsObjects()` and `getPlanesObjects()` where they draw
   * them. The parameter stays for compatibility.
   */
  constructor(private readonly graphics: HsGraphics, _queue: HsGraphicQueue) {
    super(getUuid(), 'Axis');
    this.primitives = new HsPrimitives(graphics);
  }

  public addArrow(
    rotation: HsHypersceneTypes.Array3,
    action: HsToolAction,
    color: HsColor,
    rotationAngle: number = HsHypersceneConstants.PI_DIV_2
  ): void {
    // Each tool gets its own material, since the tool sets its color on it.
    this.primitives.setDefaultMaterial(this.graphics.gl, color);
    const arrowObject: HsGraphicObject | null = this.primitives.makeArrow([0, 0, 0]);
    if (arrowObject === null) {
      return;
    }
    const arrow: HsArrowTool = new HsArrowTool(
      arrowObject.geometry,
      arrowObject.material,
      action,
      rotation,
      color,
      rotationAngle
    );
    arrow.setParent(this);
    this.arrows.push(arrow);
    this.allTools.push(arrow);
  }

  public addPlane(
    rotation: HsHypersceneTypes.Array3,
    action: HsToolAction,
    color: HsColor,
    scale: number,
    centered: boolean = false,
    rotationAngle: number = HsHypersceneConstants.PI_DIV_2
  ): void {
    this.primitives.setDefaultMaterial(this.graphics.gl, color);
    const planeObject: HsGraphicObject | null = this.primitives.makePlane([0, 0, 0]);
    if (planeObject === null) {
      return;
    }
    const plane: HsPlaneTool = new HsPlaneTool(
      planeObject.geometry,
      planeObject.material,
      action,
      rotation,
      color,
      scale,
      centered,
      rotationAngle
    );
    plane.setParent(this);
    this.planes.push(plane);
    this.allTools.push(plane);
  }

  public getAllTools(): HsControlTool[] {
    return this.allTools;
  }

  public getArrowsObjects(): HsGraphicObject[] {
    return this.arrows.map((arrow: HsControlTool) => arrow);
  }

  public getPlanesObjects(): HsGraphicObject[] {
    return this.planes.map((plane: HsControlTool) => plane);
  }

  public addObject(object: HsGraphicObject): void {
    const matrix: HsMatrix4 = HsMatrix4.makeCopy(object.worldMatrix);
    this.objectsMap.set(object, matrix);
  }

  public removeObject(object: HsGraphicObject): void {
    this.objectsMap.delete(object);
  }

  public clearObjects(): void {
    this.objectsMap.clear();
  }

  public updateBoundingSpheres(): void {
    this.arrows.forEach((tool: HsControlTool) => tool.updateBoundingSphere());
  }

  public needUpdateOrigin(): void {
    this.needToSetOrigin = true;
  }

  public doAction(axisActionType: HsToolAction, rayDirection: HsVector3, rayOrigin: HsVector3): void {
    this.position.set(0, 0, 0);
    const plane: HsPlane = this.makePlaneByMoveType(axisActionType, rayDirection);
    if (this.needToSetOrigin) {
      this.plane = plane;
    }
    HsPlane.castRay(this.plane, rayOrigin, rayDirection, this.target);
    if (this.needToSetOrigin) {
      this.originalTarget.copy(this.target);
      this.originalMatrix.copy(this.worldMatrix);
      this.needToSetOrigin = false;
    }
    this.target.subtract(this.originalTarget);
    const direction: number = axisActionType.directions[0];
    this.position.values[direction] = this.target.values[direction];
    if (axisActionType.directions.length > 1) {
      const direction1: number = axisActionType.directions[1];
      this.position.values[direction1] = this.target.values[direction1];
    }
    this.move(axisActionType, direction);

    this.updateChildrenWorldMatrix();
    this.updateBoundingSpheres();
  }

  private move(axisActionType: HsToolAction, direction: number): void {
    const matrixPosition: HsVector3 = new HsVector3(0, 0, 0).setFromMatrixPosition(this.originalMatrix.values);
    const positionToAdd: HsVector3 = HsVector3.makeZeroVector().copy(this.position);
    matrixPosition.add(positionToAdd);
    if (axisActionType.min !== null) {
      matrixPosition.values[direction] = Math.min(matrixPosition.values[direction], axisActionType.min);
    }
    if (axisActionType.max !== null) {
      matrixPosition.values[direction] = Math.max(matrixPosition.values[direction], axisActionType.max);
    }
    this.worldMatrix.setPosition(matrixPosition.values);
    this.objectsMap.forEach((_: HsMatrix4, object: HsGraphicObject) => {
      object.worldMatrix.setPosition(matrixPosition.values);
      object.updateBoundingSphere();
    });
  }

  private makePlaneByMoveType(axisActionType: HsToolAction, rayDirection: HsVector3): HsPlane {
    const planeNormal1: HsVector3 = HsVector3.makeZeroVector();
    planeNormal1.values[axisActionType.planes[0]] = -1;
    const position: HsVector3 = new HsVector3(...this.originalMatrix.getPositionArray());
    const plane1: HsPlane = new HsPlane(...planeNormal1.values, position.values[axisActionType.planes[0]]);
    if (axisActionType.planes.length === 1) {
      return plane1;
    }
    const planeNormal2: HsVector3 = HsVector3.makeZeroVector();
    planeNormal2.values[axisActionType.planes[1]] = -1;
    const plane2: HsPlane = new HsPlane(...planeNormal2.values, position.values[axisActionType.planes[1]]);
    const dot1: number = Math.abs(HsVector3.dot(rayDirection, plane1.normal));
    const dot2: number = Math.abs(HsVector3.dot(rayDirection, plane2.normal));
    if (dot2 > dot1) {
      return plane2;
    }
    return plane1;
  }
}
